import React, { useState } from 'react';
import { Modal, Select, Spin, Button } from 'antd';
import { useGetCompanies } from '../../hooks/genericHooks';

export type CompaniesSelectReturnMode = 'name' | 'object';

// Accept unified dynamic modal props
interface CompaniesSelectProps {
  open: boolean;
  value?: any;
  onOk: (value: any) => void;
  onCancel: () => void;
  options?: any[];
  /** `name` writes the company name into form fields. `object` keeps id, code, name, and label. */
  returnMode?: CompaniesSelectReturnMode;
}

type CompanySelectValue = {
  company_id: string | number;
  short_code: string;
  name: string;
  label: string;
};

const tryParseLegacyLabel = (v: any): { name: string; short_code: string } | null => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const m = s.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
  if (!m) return null;
  return { name: String(m[1] ?? '').trim(), short_code: String(m[2] ?? '').trim() };
};

const findCompanyId = (companiesList: any[], rawValue: any): string | number | null => {
  const raw = String(rawValue ?? '').trim();
  if (!raw) return null;

  const byId = companiesList.find((company: any) => String(company?.id) === raw);
  if (byId) return byId.id;

  const byName = companiesList.find((company: any) => String(company?.name ?? '').trim() === raw);
  if (byName) return byName.id;

  const parsed = tryParseLegacyLabel(raw);
  if (parsed?.short_code) {
    const byCode = companiesList.find((company: any) => String(company?.short_code ?? '').trim() === parsed.short_code);
    if (byCode) return byCode.id;
  }
  if (parsed?.name) {
    const byLegacyName = companiesList.find((company: any) => String(company?.name ?? '').trim() === parsed.name);
    if (byLegacyName) return byLegacyName.id;
  }

  if (/^\d+$/.test(raw)) return rawValue;
  return null;
};

const resolveSelectedId = (value: any, companiesList: any[]): string | number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    const record: any = value;
    if (record.company_id != null) return record.company_id;
    if (record.id != null) return record.id;
    if (record.name != null) {
      const byName = findCompanyId(companiesList, record.name);
      if (byName != null) return byName;
    }
    if (record.label != null) {
      const byLabel = findCompanyId(companiesList, record.label);
      if (byLabel != null) return byLabel;
    }
    if (record.value != null) return findCompanyId(companiesList, record.value);
    return null;
  }
  return findCompanyId(companiesList, value);
};

const CompaniesSelect: React.FC<CompaniesSelectProps> = ({
  open,
  value,
  onOk,
  onCancel,
  returnMode = 'name',
}) => {
  const { data: companiesData, isLoading } = useGetCompanies();
  const [selectedId, setSelectedId] = useState<string | number | null>(null);

  const companiesList = Array.isArray(companiesData)
    ? companiesData
    : (companiesData?.data || []);

  React.useEffect(() => {
    const nextId = resolveSelectedId(value, companiesList);
    setSelectedId((prev) => (String(prev ?? '') === String(nextId ?? '') ? prev : nextId));
  }, [value, companiesList]);

  return (
    <Modal
      open={open}
      title="Select Company"
      closable={true}
      getContainer={false}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="ok"
          type="primary"
          onClick={() => {
            const selectedCompany = companiesList.find((company: any) => String(company.id) === String(selectedId));
            if (selectedCompany) {
              const name = String(selectedCompany.name ?? '').trim();
              if (returnMode === 'object') {
                const payload: CompanySelectValue = {
                  company_id: selectedCompany.id,
                  short_code: String(selectedCompany.short_code ?? '').trim(),
                  name,
                  label: `${name} (${String(selectedCompany.short_code ?? '').trim()})`,
                };
                onOk(payload);
              } else {
                onOk(name);
              }
            }
          }}
          disabled={!selectedId}
        >
          Select
        </Button>
      ]}
      destroyOnClose
      onCancel={onCancel}
    >
      <Spin spinning={isLoading}>
        <Select
          showSearch
          style={{ width: '100%', borderRadius: 0, background: '#e6f2ff', border: 'none' }}
          placeholder="Select a company"
          value={selectedId ?? undefined}
          onChange={setSelectedId}
          optionFilterProp="children"
          dropdownStyle={{ borderRadius: 0 }}
          filterOption={(input, option) =>
            String(option?.children).toLowerCase().includes(input.toLowerCase())
          }
        >
          {companiesList.map((company: any) => (
            <Select.Option
              key={company.id}
              value={company.id}
              title={`${company.name} (${company.short_code}) - ${company.category} - ${company.area}`}
            >
              {company.name} ({company.short_code}) - {company.category} - {company.area}
            </Select.Option>
          ))}
        </Select>
      </Spin>
    </Modal>
  );
};

export default CompaniesSelect;
